<script setup lang="ts">
/********************
 * PROPS & EMITS     *
 ********************/
export interface CiFilterRatingProps {
  displayValue?: string;
  label?: string;
  max?: number;
  name?: string;
  value?: number;
}
const props = withDefaults(defineProps<CiFilterRatingProps>(), {
  displayValue: '',
  label: '',
  max: 4,
  name: '',
  value: 0,
});

const emit = defineEmits<{
  input: [value: number];
}>();

/********************
 * REFS & VARS       *
 ********************/
const rangeValue = ref(props.value);

/********************
 * WATCHER           *
 ********************/
watch(() => props.value, onValueChange, { immediate: true });

/********************
 * FUNCTIONS         *
 ********************/

function onValueChange(newValue) {
  rangeValue.value = newValue;
}

function onValueClicked(value) {
  emit('input', value === rangeValue.value ? 0 : value);
}

function getFormattedValue(rating) {
  return props.displayValue.replace(/\{([^}]+)\}/g, rating);
}

function getCircleClass(rating) {
  return rangeValue.value === rating && rangeValue.value !== 0 ? 'circle--primary' : 'circle--gray';
}
</script>

<template>
  <div class="flex break-inside-avoid flex-col">
    <label
      v-if="label.length"
      :for="name"
      class="mb-2 text-sm"
    >{{ label }}</label>

    <div class="flex w-full">
      <CiRatingRadial
        v-for="index in props.max"
        :key="index"
        :rating="index"
        :formatter="getFormattedValue(index)"
        :circle-class="getCircleClass(index)"
        class="filter-rating__radial linear mx-[3px] my-0 flex size-12 max-w-[3.125rem] transform-gpu cursor-pointer items-center justify-center transition-transform duration-200 hover:scale-110 hover:will-change-transform"
        :class="{ 'filter-rating__radial--active': rangeValue === index }"
        @click="onValueClicked(index)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter-rating__radial {
  &.rating-radial :deep(.rating-radial__circular) {
    @apply h-auto w-full;
  }

  :deep(.circular-text) {
    @apply text-sm;
  }

  &--active {
    :deep(.circular-text) {
      @apply font-bold;
    }
  }
}
</style>
